import React from 'react';
import { ArrowRight, Target, BookOpen, Users } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-[#1a1c1b] pt-20">
      {/* Hero Section */}
      <section className="relative h-screen overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/fort-margarita-kuching.jpg"
            alt="Fort Margherita Kuching - Dark Tourism Malaysia"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-[#1a1c1b]"></div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <div className="container-custom pb-20 md:pb-32">
            <div className="max-w-4xl">
              <h1 className="text-white font-light text-5xl md:text-7xl mb-6 leading-tight">
                About Dark Tourism Malaysia
              </h1>
              <p className="text-lg md:text-xl font-light text-white/90 leading-relaxed max-w-2xl">
                A platform dedicated to documenting, preserving, and raising awareness about 
                Malaysia's places connected to memory, tragedy, and historical depth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Personal Journey with Author Image */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl">
            {/* Author Image - Natasha Razak */}
            <div className="order-2 md:order-1">
              <div className="relative rounded-lg overflow-hidden">
                <img 
                  src="/images/natasha-razak.jpg"
                  alt="Natasha Razak - Founder & Researcher"
                  className="w-full h-auto object-cover rounded-lg"
                />
                <div className="mt-4 text-center">
                  <p className="text-white font-semibold text-lg">Natasha Razak</p>
                  <p className="text-[#dc2626] text-sm">Founder & Researcher</p>
                </div>
              </div>
            </div>

            {/* Text Content */}
            <div className="order-1 md:order-2">
              <h2 className="heading-2 text-[#dc2626] mb-8" data-testid="about-story-heading">Our Story</h2>
              <div className="space-y-6 body-medium text-white/80">
                <p>
                  After more than a decade in academia, I moved to Malaysia in 2019 and began my 
                  doctoral studies at Universiti Putra Malaysia with a clear purpose: to contribute 
                  to academic knowledge while also serving the country that had become my home.
                </p>
                <p>
                  While exploring research gaps in tourism studies, I encountered an unexpected reality: 
                  <span className="text-[#dc2626] font-semibold"> dark tourism in Malaysia remains largely under-researched and often overlooked</span>. 
                  Around the world, nations preserve and present difficult histories as part of their 
                  collective memory and identity. In Malaysia, many meaningful sites and stories remain 
                  hidden from public awareness or are quietly fading from national consciousness.
                </p>
                <p>
                  With the support of government agencies and tourism authorities, I began a nationwide 
                  exploration of these overlooked narratives. Over the course of my doctoral research, 
                  I identified more than 250 dark tourism sites throughout Malaysia. Some have no signage. 
                  Others exist in plain sight yet receive little to no recognition.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section with Background Image */}
      <section className="relative py-32 overflow-hidden bg-[#1a1c1b]">
        <div className="absolute inset-0">
          <img 
            src="/images/kellies-castle.jpg"
            alt="Kellie's Castle, Perak"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1c1b] via-[#1a1c1b]/90 to-transparent"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl">
            <p className="text-white/90 text-2xl md:text-3xl italic border-l-4 border-[#dc2626] pl-8 leading-relaxed">
              "What began as academic research gradually became a personal commitment to uncover 
              and honour histories that deserve greater visibility."
            </p>
          </div>
        </div>
      </section>

      {/* Continued Story */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="max-w-4xl">
            <div className="space-y-6 body-medium text-white/80">
              <p>
                What began as academic research gradually became a personal commitment to uncover 
                and honour histories that deserve greater visibility. I completed my PhD with a focus 
                on dark tourism in Malaysia, and this work continues beyond the university.
              </p>
              <p>
                Through conversations, fieldwork, and public engagement, I came to see how strongly 
                people connect with these stories when they are shared with care and respect. This 
                site continues that journey by sharing these discoveries with a wider audience. It is 
                created for Malaysians and for anyone with an interest in places shaped by memory, 
                history, and meaning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Cards */}
      <section className="space-section bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="max-w-6xl">
            <div className="mb-16">
              <h2 className="heading-2 text-[#dc2626] mb-4">Mission</h2>
              <p className="body-large text-white/80 max-w-3xl">
                This platform exists to share and preserve places across Malaysia connected to 
                memory, loss, conflict, and historical depth.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-[#302f2c] p-8 rounded-lg border border-[#3a2222]">
                <Target className="text-[#dc2626] mb-4" size={40} />
                <h3 className="heading-4 text-white mb-4">Raise Awareness</h3>
                <p className="body-small text-white/70">
                  Bring visibility to Malaysia's dark tourism heritage and its cultural significance.
                </p>
              </div>

              <div className="bg-[#302f2c] p-8 rounded-lg border border-[#3a2222]">
                <BookOpen className="text-[#dc2626] mb-4" size={40} />
                <h3 className="heading-4 text-white mb-4">Encourage Reflection</h3>
                <p className="body-small text-white/70">
                  Foster thoughtful reflection, respect, and understanding of the past.
                </p>
              </div>

              <div className="bg-[#302f2c] p-8 rounded-lg border border-[#3a2222]">
                <Users className="text-[#dc2626] mb-4" size={40} />
                <h3 className="heading-4 text-white mb-4">Share Stories</h3>
                <p className="body-small text-white/70">
                  Document research, news, and real stories ensuring histories remain visible.
                </p>
              </div>

              <div className="bg-[#302f2c] p-8 rounded-lg border border-[#3a2222]">
                <ArrowRight className="text-[#dc2626] mb-4" size={40} />
                <h3 className="heading-4 text-white mb-4">Connect People</h3>
                <p className="body-small text-white/70">
                  Link Malaysians and international visitors with stories behind places that shaped the nation.
                </p>
              </div>
            </div>
          </div>

          {/* CTA with Image */}
          <div className="mt-16 relative rounded-lg overflow-hidden">
            <img 
              src="/images/wang-kelian-perlis.jpg"
              alt="Wang Kelian, Perlis - Dark Tourism Malaysia"
              className="w-full h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent"></div>
            <div className="absolute inset-0 p-8 md:p-12 flex items-center">
              <div className="max-w-2xl">
                <h3 className="heading-3 text-white mb-4">Explore Dark Tourism Sites</h3>
                <p className="body-medium text-white/90 mb-6">
                  Discover over 250 documented sites across Malaysia connected to war, conflict, and memory.
                </p>
                <a href="/explore" className="btn-primary">
                  View All Sites
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="max-w-4xl">
            <h2 className="heading-2 text-[#dc2626] mb-8" data-testid="what-we-do-heading">What We Do</h2>
            <p className="body-medium text-white/80 mb-12">
              This platform documents and presents sites across Malaysia connected to memory, tragedy, 
              conflict, and difficult histories. It brings together academic research, field exploration, 
              and public contributions to create a carefully curated record of places that are often 
              overlooked or misunderstood.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-[#1a1c1b] p-6 rounded-lg border border-[#3a2222]">
                <h3 className="heading-4 text-[#dc2626] mb-3">Research & Documentation</h3>
                <p className="body-small text-white/70">
                  Locations across Malaysia are identified, studied, and documented through ongoing research 
                  and site visits. Each place is approached with attention to historical accuracy, cultural 
                  context, and local sensitivity.
                </p>
              </div>

              <div className="bg-[#1a1c1b] p-6 rounded-lg border border-[#3a2222]">
                <h3 className="heading-4 text-[#dc2626] mb-3">Preservation Through Storytelling</h3>
                <p className="body-small text-white/70">
                  Many historically significant sites remain unmarked, under-researched, or known only 
                  through fragmented accounts. By documenting them and sharing their narratives, this 
                  platform contributes to preserving memory and encouraging wider awareness.
                </p>
              </div>

              <div className="bg-[#1a1c1b] p-6 rounded-lg border border-[#3a2222]">
                <h3 className="heading-4 text-[#dc2626] mb-3">Public Talks & Educational Outreach</h3>
                <p className="body-small text-white/70">
                  Beyond this website, the work extends into lectures, academic presentations, and invited 
                  talks delivered across Malaysia. These sessions focus on raising awareness of dark tourism 
                  as part of cultural and historical heritage.
                </p>
              </div>

              <div className="bg-[#1a1c1b] p-6 rounded-lg border border-[#3a2222]">
                <h3 className="heading-4 text-[#dc2626] mb-3">Public Contributions & Local Knowledge</h3>
                <p className="body-small text-white/70">
                  Local insight plays an important role. Visitors, residents, and researchers are invited 
                  to share information about forgotten sites, community histories, and personal memories 
                  connected to places of significance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before It Disappears */}
      <section className="space-section bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="max-w-4xl">
            <h2 className="heading-2 text-[#dc2626] mb-8" data-testid="before-disappears-heading">Before It Disappears</h2>
            <div className="space-y-6 body-medium text-white/80">
              <p className="text-white/90 text-xl italic">
                We often move through places without realizing what once stood there, who lived there, 
                or what events unfolded on the same ground. Across Malaysia, traces of earlier lives and 
                difficult histories are slowly disappearing from view.
              </p>
              
              <div className="bg-[#302f2c] p-8 rounded-lg border-l-4 border-[#dc2626]">
                <ul className="space-y-4 body-medium text-white/80">
                  <li>Along parts of <strong>Kelantan's coastline</strong>, small concrete bunkers from the Second World War still stand quietly. These British pillboxes were built in anticipation of Japanese invasion. Many are slowly sinking into the ground.</li>
                  <li><strong>Pudu Jail</strong> in Kuala Lumpur, once a place of confinement for some of Malaysia's most dangerous offenders, was demolished during urban redevelopment. Today, only its entrance gate remains.</li>
                  <li><strong>Bidong Island</strong>, off the coast of Terengganu, once hosted thousands of Vietnamese refugees who arrived by boat seeking safety. Today, the island stands largely quiet, its structures weathered and slowly reclaimed by nature.</li>
                  <li><strong>Jerejak Island</strong>, once used as a quarantine station, leprosy settlement, and later a prison, reflects layered histories of isolation and resilience.</li>
                  <li>In parts of <strong>Sabah and Sarawak</strong>, burial poles and sites linked to former headhunting traditions remain as anthropological traces of cultural practices that once shaped community life.</li>
                  <li>Across the country, smaller <strong>burial grounds</strong> and resting places of notable individuals are becoming overgrown or difficult to locate, kept alive mainly through local knowledge.</li>
                </ul>
              </div>

              <p className="text-white/90 text-lg italic border-l-4 border-[#dc2626] pl-6">
                "When these places fade, it is not only names on gravestones that disappear. 
                Whole chapters of lived experience begin to slip away."
              </p>

              <p>
                Stories of crime and justice, war and survival, illness and isolation, migration and loss 
                are often tied to specific locations. Some are remembered through official records, but many 
                survive through families, neighbours, and community memory. When the physical sites vanish, 
                the details soften. The lessons become harder to pass on.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="max-w-4xl">
            <h2 className="heading-2 text-[#dc2626] mb-6">Join Us in Preservation</h2>
            <p className="body-large text-white/80 mb-8">
              Whether you're a researcher, heritage enthusiast, or someone with a story to share, 
              we invite you to be part of this important work.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="/share-story" className="btn-primary">
                Share Your Story <ArrowRight className="ml-2" size={20} />
              </a>
              <a href="/contact" className="btn-secondary">
                Get in Touch
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
